import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { ConnectionsModule } from '@modules/connections/connections.module';
import { type Connection } from '@modules/connections/types/connection';
import { SimpleInputComponent } from '@modules/form-controls/components/simple-input/simple-input.component';
import { KnotsModule } from '@modules/knots/knots.module';
import { type Knot } from '@modules/knots/types/knot';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { TagsModule } from '@modules/tags/tags.module';
import { type Tag } from '@modules/tags/types/tag';
import { FormsWorkspaceStore } from '../services/forms-workspace.store';
import { computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';

/**
 * The list's own board search (a pane header's, as in a scoped or embedded list). Laid out on the shared search popover (keyword row, then Cancel / Search);
 * boards are searched locally by title and description, so there is no advanced form or knot suggestions to
 * ADD a marker here (the sidebar's own Knots/Tags panels are where one gets picked, per devdoc/specs/boards.md).
 * The markers a pick there already applied ARE shown here, though — with their own remove control, same as
 * the shared search popover — since they narrow this exact list and, until now, offered no way to see which
 * ones were active or drop just one without clearing the whole search (SBUX-988 review).
 */
@Component({
  selector: 'app-forms-search-popover',
  templateUrl: './forms-search-popover.component.html',
  styleUrls: ['../../search/components/search-popover/search-popover.component.less'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SimpleInputComponent, Button2Component, KnotsModule, TagsModule, ConnectionsModule],
})
export class FormsSearchPopoverComponent {
  public readonly width = input(420);
  /** Asked before a search is applied (the host's pending-save and dirty-Details guard); `false` keeps the popover and the typed draft as they are. */
  public readonly beforeApply = input<() => boolean | Promise<boolean>>(() => true);
  public readonly apply = input.required<(query: string) => Promise<boolean>>();
  public readonly onSearch = output<void>();
  public readonly onCancel = output<void>();

  private readonly store = inject(FormsWorkspaceStore);
  private readonly searchService = inject(AdvancedSearchService, { optional: true });
  /** Seeded from `effectiveSearch()`, not the store's own local `search` — otherwise a term inherited
   * from another app's search (carried on the shared `AdvancedSearchService`, no local term of its own)
   * opened this field blank while the list was, in fact, still filtered by it (SBUX-988 review). Seeded
   * once, when the popover opens: the field is the user's draft from then on. */
  private readonly state = toSignal(this.searchService?.getState() ?? of(undefined));
  protected readonly listStore = { markers: computed(() => ({ knots: this.state()?.knots ?? [], tags: this.state()?.tags ?? [], connections: this.state()?.connections ?? [] })) };
  protected readonly inputControl = new FormControl(this.store.search().trim() || this.searchService?.getStateSync()?.query || '', { nonNullable: true });

  protected async handleSearch(): Promise<void> {
    if (!(await this.beforeApply()())) return;
    if (await this.apply()(this.inputControl.value.trim())) this.onSearch.emit();
  }

  protected handleCancel(): void {
    this.onCancel.emit();
  }

  protected removeKnowledgeItem(item: Knot | Tag | Connection): void {
    this.searchService?.removeKnowledgeItem(item);
  }
}
